import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Upload, FileText, Trash2, Download, FolderInput, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

export default function SupplierDocuments() {
  const today = new Date();
  const defaultMonth = today.getMonth() === 0 ? 12 : today.getMonth();
  const defaultYear = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();

  const [year, setYear] = useState(defaultYear);
  const [month, setMonth] = useState(defaultMonth);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [docType, setDocType] = useState("statement");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supplier-documents`, {
        params: { year, month },
      });
      setDocs(res.data.documents || []);
    } catch (e) {
      toast.error("Failed to load documents: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      const list = Array.isArray(res.data) ? res.data : (res.data.suppliers || []);
      setSuppliers(list.filter(s => s.active !== false).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
    } catch (e) {
      toast.error("Failed to load suppliers: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => { fetchSuppliers(); }, []);
  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [year, month]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!selectedSupplierId) {
      toast.error("Pick a supplier first");
      e.target.value = "";
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB)");
      e.target.value = "";
      return;
    }
    const supplier = suppliers.find(s => s.id === selectedSupplierId);
    const reader = new FileReader();
    reader.onload = async () => {
      const b64 = reader.result.split(",", 2)[1];
      setUploading(true);
      try {
        await axios.post(`${API}/supplier-documents`, {
          supplier_id: selectedSupplierId,
          supplier_name: supplier?.name,
          doc_type: docType,
          period_year: year,
          period_month: month,
          filename: file.name,
          content_b64: b64,
        });
        toast.success(`Uploaded ${file.name}`);
        await fetchDocs();
      } catch (err) {
        toast.error("Upload failed: " + (err.response?.data?.detail || err.message));
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/supplier-documents/${id}`);
      toast.success("Deleted");
      await fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const handleDownload = (id, filename) => {
    const a = document.createElement("a");
    a.href = `${API}/supplier-documents/${id}/download`;
    a.download = filename;
    a.click();
  };

  const statementCount = docs.filter(d => d.doc_type === "statement").length;
  const invoiceCount = docs.filter(d => d.doc_type === "invoice").length;

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="supplier-docs-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FolderInput className="h-6 w-6 text-purple-600" />
          Supplier Documents
        </h1>
        <p className="text-gray-500">
          Upload supplier statements and invoices here so they get bundled into the Monthly Accountant Pack.
        </p>
      </div>

      {/* Period */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-lg">1. Period</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 max-w-md">
          <div>
            <Label>Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid="docs-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>{yearOptions.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid="docs-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">2. Upload</CardTitle>
          <CardDescription>PDF / CSV / XLSX up to 15 MB. Re-uploading the same filename replaces the existing entry.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
            <div className="md:col-span-2">
              <Label>Supplier</Label>
              <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
                <SelectTrigger data-testid="docs-supplier-select">
                  <SelectValue placeholder="Pick a supplier…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {suppliers.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} {s.supplier_number ? `(${s.supplier_number})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="docs-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="statement">Supplier Statement</SelectItem>
                  <SelectItem value="invoice">Supplier Invoice</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg"
            onChange={handleUpload}
            className="hidden"
            data-testid="docs-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !selectedSupplierId}
            data-testid="docs-upload-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Uploading…" : "Choose & Upload File"}
          </Button>
        </CardContent>
      </Card>

      {/* Files this period */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">3. Files for {MONTHS[month - 1]} {year}</CardTitle>
              <CardDescription>
                Statements: <span className="font-mono">{statementCount}</span> ·
                Invoices: <span className="font-mono">{invoiceCount}</span>
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchDocs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {docs.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
              No supplier documents uploaded for this period yet.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map(d => (
                  <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                    <TableCell className="font-medium">{d.supplier_name}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        d.doc_type === "statement"
                          ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-800"
                      }`}>
                        {d.doc_type}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{d.filename}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {(d.file_size / 1024).toFixed(1)} KB
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : ""}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleDownload(d.id, d.filename)}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => handleDelete(d.id)}
                        className="text-red-600"
                        title="Delete"
                        data-testid={`delete-doc-${d.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
